import Button from "@builder/components/general/Button";
import OliverText from "@builder/components/general/OliverText";
import { Action, ActionName } from "@common/build/types";
import "./style.scss";
import Toggle from "@builder/components/general/Toggle";
import { Fragment, useEffect, useState } from "react";
import {
    convertConditionsToString,
    getActionType,
    getConditionActionValue,
    isEmpty,
    parseConditionString,
    validateConditions
} from "@builder/utils/conditionsHelpers";
import useConditions, { ConditionState } from "@builder/hooks/useConditions";
import { ConditionsDataProvider } from "@builder/context/ConditionsData";
import ConditionSelectors from "@builder/components/general/ConditionSelectors";
import SelectInput from "@builder/components/general/ConditionSelectInput";
import { actionSelectOptions } from "../Popup/popups/Conditions/utils";
import { CONDITION_ACTION } from "@builder/utils/constants";
import { v4 } from "uuid";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { removeConditionalActions, setCondition, updateConditionalActions } from "@builder/store/slices/BuilderSlice/builderSlice";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";

const ConditionsComponent = () => {
    const dispatch = useAppDispatch();
    const selectedNode = useAppSelector(getSelectedNode);
    const condition: ConditionState | undefined =
        selectedNode?.conditions && selectedNode?.conditions?.length > 0
            ? parseConditionString(selectedNode.conditions[0].conditions || "")
            : undefined;
    const a = selectedNode?.actions?.find(action => action.id === selectedNode.conditions?.[0]?.actionId);
    const { state, handleSelect, addCondition, addGroup, removeCondition, removeAllConditions } = useConditions(
        condition || undefined,
        selectedNode?.id
    );
    const [conditionActionValue, setConditionActionValue] = useState<string>(getConditionActionValue(a?.action.type || null));
    const [isToggle, setToggle] = useState(selectedNode?.conditions ? true : false);

    const saveCondition = () => {
        const newConditionString = convertConditionsToString(state);
        const existingConditionString = selectedNode?.conditions?.[0]?.conditions || "";

        if (newConditionString !== existingConditionString) {
            const action = getActionType(conditionActionValue as CONDITION_ACTION);

            const conditionAction: Action = {
                id: v4(),
                name: ActionName.ON_CONDITION,
                action: action
            };

            const condition: { id: string; name: string; conditions: string; actionId: string } = {
                id: v4(),
                name: "condition name",
                conditions: newConditionString,
                actionId: conditionAction.id
            };

            dispatch(setCondition(condition));
            dispatch(updateConditionalActions(conditionAction));
        }
    };

    useEffect(() => {
        const errorStates = validateConditions(state);

        if (!isEmpty(errorStates) && isToggle) {
            const initialCondition = {
                id: v4(),
                name: "",
                conditions: "([]  [])",
                actionId: ""
            };
            dispatch(setCondition(initialCondition));
            dispatch(removeConditionalActions(undefined));
            return;
        } else if (!isToggle) {
            dispatch(setCondition(undefined));
            dispatch(removeConditionalActions(undefined));
            return;
        } else {
            saveCondition();
        }
    }, [state, isToggle]);

    return (
        <div className="no-conditions">
            <div className="condition-toggle-wrapper">
                <OliverText text="Turn on conditions" size="xs" weight="medium" translatePrefix="conditions." />
                <Toggle checked={isToggle} callback={setToggle} className="toggle-conditions" />
            </div>

            {isToggle ? (
                <div className="condition-info-wrapper">
                    <OliverText text="If conditions apply" size="xs" weight="medium" translatePrefix="conditions." />
                    <OliverText
                        text="Choose what will happen if the conditions you set applies to the element"
                        size="xxs"
                        weight="medium"
                        translatePrefix="conditions."
                    />
                    <SelectInput
                        value={conditionActionValue}
                        callback={value => {
                            removeAllConditions();
                            setConditionActionValue(value);
                        }}
                        options={actionSelectOptions}
                        className="condition-action-dropdown"
                    />
                    <div className="condition-divider" />
                    <div className="conditions-body">
                        {state.conditions.map((condition, groupIndex) => (
                            <Fragment key={groupIndex}>
                                <div className="conditions-selectors-wrapper">
                                    <ConditionsDataProvider>
                                        <ConditionSelectors
                                            error={null}
                                            isEdit={false}
                                            conditions={condition}
                                            handleSelect={(index, key, value) => handleSelect(groupIndex, index, key, value)}
                                            removeCondition={index => removeCondition(groupIndex, index)}
                                            addCondition={() => addCondition(groupIndex)}
                                            groupIndex={groupIndex}
                                        />
                                    </ConditionsDataProvider>
                                </div>
                                {condition && condition.length > 0 && groupIndex < state.conditions.length - 1 && (
                                    <div className="decision-selectors">
                                        <OliverText text="Or" size="xs" weight="medium-heavy" translatePrefix="popup.actionBuilder." />
                                    </div>
                                )}
                            </Fragment>
                        ))}
                        <div className="decision-selectors">
                            <Button
                                onClick={addGroup}
                                buttonStyle="secondary"
                                text="Or"
                                size="small"
                                style={{ width: "100%", height: "32px", marginBottom: "8px" }}
                            />
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ConditionsComponent;
